import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { GetPrograms200 } from '@eureca/recruiter-api-sdk/types';

type ProgramListEntry = GetPrograms200['items'][number];

const ACTIVE_STATUS_KEYS: ReadonlySet<ProgramListEntry['statusKey']> = new Set([
  'published',
  'private',
]);

export interface CheckActiveProgramNameTakenArgs {
  name: string;
  companyId: string;
}

/**
 * Checks if there is already an ACTIVE program in the given company with the
 * same name. "Active" means `statusKey ∈ {published, private}` — `not_published`
 * (rascunho), `cancelled` and `inactivated` programs do NOT block creation
 * (per EU-150 RN-03).
 *
 * Match is case-insensitive after trim. The BFF /programs `search` param is
 * LIKE/ILIKE-based, so we still need to compare exact name on the client.
 *
 * Fail-open: any network/upstream error returns `false` so the user is never
 * blocked from advancing by an outage. The 5-minute window between Step 1 and
 * the final POST is short enough that the risk of a stale "free" is low; if
 * the user manages to slip through, the eventual Core 409 (when wired in
 * eureca-core) still catches it.
 */
export async function checkActiveProgramNameTaken({
  name,
  companyId,
}: CheckActiveProgramNameTakenArgs): Promise<boolean> {
  const trimmed = name.trim();
  // BFF requires search.min(2); below that we just skip the round-trip and let
  // the field's own `min(1)` validator surface the empty-name error.
  if (trimmed.length < 2) return false;
  if (!companyId) return false;

  try {
    const res = await fetchClient<GetPrograms200>({
      method: 'GET',
      url: '/programs',
      params: {
        companyId,
        search: trimmed,
        // Max BFF page size — names that happen to share a search-substring
        // pattern can produce many candidates; we want exact-name match
        // visible in the first response page so we don't paginate.
        pageSize: 100,
      },
    });
    if (res.error || !res.data) return false;

    const needle = trimmed.toLowerCase();
    return res.data.items.some(
      (item) => ACTIVE_STATUS_KEYS.has(item.statusKey) && item.name.trim().toLowerCase() === needle
    );
  } catch {
    return false;
  }
}
